import { useState } from "react";
import { Loader2, Printer, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PrinterSetup } from "@/components/docs/PrinterSetup";
import { getPrinter, qzPrintRaw, qzErrorText } from "@/lib/qz";

async function blobError(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { return JSON.parse(await d.text()).detail || "Gagal membuat PDF"; } catch { return "Gagal membuat PDF"; }
  }
  return e?.response ? apiError(e) : qzErrorText(e);
}

function reportSkipped(errors) {
  if (!errors?.length) return;
  toast.warning(`${errors.length} dokumen dilewati: ${errors.slice(0, 3).map((x) => `${x.number || ""} ${x.error}`.trim()).join("; ")}${errors.length > 3 ? "…" : ""}`);
}

// Dark bulk bar: print many documents in one raw dot-matrix job or download one merged PDF.
export function BulkPrintBar({ count, summary, body, onClear, children, testId }) {
  const [busy, setBusy] = useState(null);
  const [setup, setSetup] = useState(false);
  if (!count) return null;

  const print = async () => {
    const printer = getPrinter();
    if (!printer) { toast.info("Pilih printer dot-matrix dulu"); setSetup(true); return; }
    setBusy("print");
    try {
      const r = await api.post("/documents/batch/raw", body());
      const d = r.data.data;
      await qzPrintRaw(printer, d.base64, `Cetak massal ${d.count} dokumen`);
      toast.success(`${d.count} dokumen (${d.pages} halaman) dikirim ke ${printer}`);
      reportSkipped(d.errors);
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(null); }
  };

  const pdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.post("/documents/batch/pdf", body(), { responseType: "blob" });
      const name = ((r.headers["content-disposition"] || "").match(/filename="([^"]+)"/) || [])[1] || "Dokumen-massal.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
      toast.success(`PDF gabungan ${r.headers["x-doc-count"] || ""} dokumen diunduh`);
      const skipped = Number(r.headers["x-doc-errors"] || 0);
      if (skipped) toast.warning(`${skipped} dokumen dilewati (belum ada DO / tidak punya akses)`);
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="bulk-bar" data-testid={testId}>
      <span>{summary}</span>
      {children}
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={onClear} data-testid={`${testId}-clear`}><X size={14} /> Batal pilih</button>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={pdf} disabled={!!busy} data-testid={`${testId}-pdf`}>
        {busy === "pdf" ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} Unduh PDF gabungan</button>
      <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={print} disabled={!!busy} data-testid={`${testId}-print`}>
        {busy === "print" ? <Loader2 size={14} className="spin" /> : <Printer size={14} />} Print dot-matrix</button>
      {setup && <PrinterSetup onClose={() => setSetup(false)} />}
    </div>
  );
}
